import type { FormEvent, ReactElement, ReactNode } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import type { CreditAdminResponse, CreditLimitStatus, CreditRule } from '@shared/api';
import { ApiError, getCreditAdmin, lookupCreditAgency, refundCredit, saveCreditRules } from '../api/client';
import { useShellState } from '../components/AppShell';
import {
  WINDOW_OPTIONS,
  agencyDraftFrom,
  agencyLabel,
  applyAgencyDraft,
  applyLimitDraft,
  describeAllowance,
  describeUsage,
  emptyAgencyDraft,
  emptyLimitDraft,
  groupRules,
  limitDraftFrom,
  removeAgency,
  savedName,
  timezoneLabel,
  type AgencyDraft,
  type AgencyGroup,
  type LimitDraft,
  type RuleWindow,
} from '../lib/creditAdmin';

/** What's open: one form at a time. */
type Editing =
  | { kind: 'agency'; draft: AgencyDraft; isNew: boolean }
  | { kind: 'limit'; draft: LimitDraft; organizationId: string | null; everyRep: boolean; id: string | null };

/**
 * Proposal credits admin (docs/limits.md "Admin screen"): one card per agency
 * with its shared total, its per-rep limit and any named-rep exceptions, plus
 * recent spends with refund. The server decides who gets in — anyone else gets
 * its 403 message here.
 */
export function CreditsAdminView(): ReactElement {
  useShellState({});
  const [data, setData] = useState<CreditAdminResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    getCreditAdmin()
      .then((next) => {
        if (live) setData(next);
      })
      .catch((cause: unknown) => {
        if (live) setLoadError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, []);

  const agencyNames = useMemo(() => {
    const names = new Map<string, string | null>();
    for (const agency of data?.agencies ?? []) names.set(agency.id, agency.name);
    return names;
  }, [data]);
  const agencyName = (id: string) => agencyNames.get(id) ?? null;

  if (loadError) {
    return (
      <main className="credits-admin">
        <h1 className="credits-admin-title">Proposal credits</h1>
        <p className="credits-admin-error">{loadError}</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="credits-admin" aria-busy="true">
        <h1 className="credits-admin-title">Proposal credits</h1>
        <p className="credits-admin-muted">Loading…</p>
      </main>
    );
  }

  const rules = data.rules.rules;
  const groups = groupRules(rules, agencyName);
  const nameFor = (group: AgencyGroup) => agencyName(group.organizationId) ?? savedName(group);

  const writeRules = async (next: CreditRule[]): Promise<boolean> => {
    setSaving(true);
    try {
      setData(await saveCreditRules({ ...data.rules, rules: next }));
      return true;
    } catch (cause) {
      const message = cause instanceof ApiError || cause instanceof Error ? cause.message : String(cause);
      if (editing) setFormError(message);
      else setActionError(message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const open = (next: Editing) => {
    setFormError(null);
    setActionError(null);
    setEditing(next);
  };
  const close = () => {
    setEditing(null);
    setFormError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const result =
      editing.kind === 'agency'
        ? applyAgencyDraft(rules, editing.draft, editing.isNew)
        : applyLimitDraft(
            rules,
            editing.draft,
            {
              organizationId: editing.organizationId,
              organizationName: editing.organizationId ? agencyName(editing.organizationId) : null,
              everyRep: editing.everyRep,
            },
            editing.id,
          );
    if ('error' in result) {
      setFormError(result.error);
      return;
    }
    if (await writeRules(result.rules)) close();
  };

  const confirmRemove = async (question: string, next: CreditRule[]) => {
    if (!window.confirm(question)) return;
    setActionError(null);
    await writeRules(next);
  };

  const refund = async (proposalId: string, label: string) => {
    if (!window.confirm(`Give back the credit for “${label}”?`)) return;
    setActionError(null);
    try {
      setData(await refundCredit(proposalId));
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  // A stale error would blame the old input, so any edit clears it.
  const setAgencyDraft = (patch: Partial<AgencyDraft>) => {
    if (!('organizationName' in patch && Object.keys(patch).length === 1)) setFormError(null);
    setEditing((current) => (current?.kind === 'agency' ? { ...current, draft: { ...current.draft, ...patch } } : current));
  };
  const setLimitDraft = (patch: Partial<LimitDraft>) => {
    setFormError(null);
    setEditing((current) => (current?.kind === 'limit' ? { ...current, draft: { ...current.draft, ...patch } } : current));
  };

  const formActions = (
    <FormActions saving={saving} error={formError} onCancel={close} />
  );

  const agencyForm = (isNew: boolean) =>
    editing?.kind === 'agency' && editing.isNew === isNew ? (
      <AgencyForm
        draft={editing.draft}
        isNew={isNew}
        agencies={data.agencies}
        onChange={setAgencyDraft}
        onSubmit={(event) => void submit(event)}
        actions={formActions}
      />
    ) : null;

  const limitForm = (organizationId: string | null, everyRep: boolean, id: string | null) =>
    editing?.kind === 'limit' &&
    editing.organizationId === organizationId &&
    editing.everyRep === everyRep &&
    editing.id === id ? (
      <LimitForm
        draft={editing.draft}
        askEmail={!everyRep}
        lockEmail={id !== null}
        onChange={setLimitDraft}
        onSubmit={(event) => void submit(event)}
        actions={formActions}
      />
    ) : null;

  const exceptionRow = (rule: CreditRule, organizationId: string | null) => (
    <Fragment key={rule.id}>
      {limitForm(organizationId, false, rule.id) ?? (
      <div className="credits-card-exception">
        <span className="credits-card-exception-who">{rule.username}</span>
        <span>{describeAllowance(rule)}</span>
        {rule.note ? <span className="credits-admin-muted">{rule.note}</span> : null}
        <span className="credits-card-actions">
          <button
            type="button"
            className="credits-admin-link"
            disabled={saving}
            onClick={() => open({ kind: 'limit', draft: limitDraftFrom(rule), organizationId, everyRep: false, id: rule.id })}
          >
            Edit
          </button>
          <button
            type="button"
            className="credits-admin-link credits-admin-link-danger"
            disabled={saving}
            onClick={() =>
              void confirmRemove(
                `Remove ${rule.username}'s own limit?`,
                rules.filter((r) => r.id !== rule.id),
              )
            }
          >
            Remove
          </button>
        </span>
      </div>
      )}
    </Fragment>
  );

  const addExceptionLink = (organizationId: string | null) =>
    limitForm(organizationId, false, null) ?? (
      <button
        type="button"
        className="credits-admin-link credits-card-add"
        disabled={saving}
        onClick={() => open({ kind: 'limit', draft: emptyLimitDraft(), organizationId, everyRep: false, id: null })}
      >
        + Give one rep their own limit
      </button>
    );

  const everywhereRule = groups.everywhere.perRep;
  const showEverywhere = Boolean(everywhereRule || groups.everywhere.reps.length || (editing?.kind === 'limit' && editing.organizationId === null));

  return (
    <main className="credits-admin">
      <header className="credits-admin-header">
        <div>
          <h1 className="credits-admin-title">Proposal credits</h1>
          <p className="credits-admin-muted">
            One credit is one proposal, spent at its first build. A rep can build while they're under every limit that
            applies to them. Reps with no limit are unlimited. Limits reset at midnight {timezoneLabel(data.rules.timezone)}.
          </p>
        </div>
        {!editing ? (
          <button type="button" className="credits-admin-primary" onClick={() => open({ kind: 'agency', draft: emptyAgencyDraft(), isNew: true })}>
            Add agency
          </button>
        ) : null}
      </header>

      {actionError ? <p className="credits-admin-error">{actionError}</p> : null}

      {agencyForm(true)}

      <section className="credits-admin-section">
        {groups.agencies.length === 0 && !showEverywhere ? (
          <p className="credits-admin-muted">No limits yet. Every rep is unlimited.</p>
        ) : null}
        <div className="credits-cards">
          {groups.agencies.map((group) => {
            const name = nameFor(group);
            const editingThis = editing?.kind === 'agency' && !editing.isNew && editing.draft.organizationId === group.organizationId;
            return (
              <article key={group.organizationId} className="credits-card">
                <header className="credits-card-header">
                  <h2 className="credits-card-title">
                    {name ?? `Agency ${group.organizationId}`}
                    <span className="credits-card-id">org {group.organizationId}</span>
                  </h2>
                  {!editingThis ? (
                    <span className="credits-card-actions">
                      <button
                        type="button"
                        className="credits-admin-link"
                        disabled={saving}
                        onClick={() => open({ kind: 'agency', draft: agencyDraftFrom(group, name), isNew: false })}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="credits-admin-link credits-admin-link-danger"
                        disabled={saving}
                        onClick={() =>
                          void confirmRemove(
                            `Remove every limit for ${name ?? group.organizationId}? Its reps become unlimited.`,
                            removeAgency(rules, group.organizationId),
                          )
                        }
                      >
                        Remove
                      </button>
                    </span>
                  ) : null}
                </header>

                {editingThis ? (
                  agencyForm(false)
                ) : (
                  <dl className="credits-card-rows">
                    <Row label="Total">
                      {group.pool ? <PoolUsage rule={group.pool} usage={data.usage[group.pool.id]} /> : <Unset>No shared limit</Unset>}
                    </Row>
                    <Row label="Per rep">
                      {group.perRep ? describeAllowance(group.perRep) : <Unset>No per-rep limit</Unset>}
                    </Row>
                    {group.pool?.note || group.perRep?.note ? (
                      <Row label="Note">{group.pool?.note ?? group.perRep?.note}</Row>
                    ) : null}
                  </dl>
                )}

                {!editingThis ? (
                  <div className="credits-card-exceptions">
                    {group.exceptions.map((rule) => exceptionRow(rule, group.organizationId))}
                    {addExceptionLink(group.organizationId)}
                  </div>
                ) : null}
              </article>
            );
          })}

          {showEverywhere ? (
            <article className="credits-card">
              <header className="credits-card-header">
                <h2 className="credits-card-title">Every agency</h2>
                {everywhereRule && !limitForm(null, true, everywhereRule.id) ? (
                  <span className="credits-card-actions">
                    <button
                      type="button"
                      className="credits-admin-link"
                      disabled={saving}
                      onClick={() =>
                        open({ kind: 'limit', draft: limitDraftFrom(everywhereRule), organizationId: null, everyRep: true, id: everywhereRule.id })
                      }
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="credits-admin-link credits-admin-link-danger"
                      disabled={saving}
                      onClick={() =>
                        void confirmRemove('Remove the limit for each rep in every agency?', rules.filter((r) => r.id !== everywhereRule.id))
                      }
                    >
                      Remove
                    </button>
                  </span>
                ) : null}
              </header>
              {limitForm(null, true, everywhereRule?.id ?? null) ?? (
                <dl className="credits-card-rows">
                  <Row label="Per rep">
                    {everywhereRule ? (
                      <>
                        {describeAllowance(everywhereRule)}
                        <span className="credits-admin-muted"> · where the agency sets no per-rep limit</span>
                      </>
                    ) : (
                      <Unset>No limit</Unset>
                    )}
                  </Row>
                </dl>
              )}
              <div className="credits-card-exceptions">
                {groups.everywhere.reps.map((rule) => exceptionRow(rule, null))}
                {addExceptionLink(null)}
              </div>
            </article>
          ) : null}
        </div>

        {!showEverywhere && !editing ? (
          <button
            type="button"
            className="credits-admin-link credits-card-add credits-everywhere-add"
            onClick={() => open({ kind: 'limit', draft: emptyLimitDraft(), organizationId: null, everyRep: true, id: null })}
          >
            + Limit every rep in every agency
          </button>
        ) : null}
      </section>

      <section className="credits-admin-section">
        <h2 className="credits-admin-subtitle">Recent credits</h2>
        {data.recent.length === 0 ? (
          <p className="credits-admin-muted">No proposal has been built since credits started.</p>
        ) : (
          <div className="credits-admin-table-wrap">
            <table className="credits-admin-table">
              <thead>
                <tr>
                  <th>Built</th>
                  <th>Proposal</th>
                  <th>Rep</th>
                  <th>Agency</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {data.recent.map((row) => {
                  const label = row.proposalName ?? row.proposalId;
                  return (
                    <tr key={`${row.proposalId}-${row.at}`} className={row.refunded ? 'credits-admin-refunded' : undefined}>
                      <td>
                        {new Date(row.at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </td>
                      {/* Plain text: a proposal opens only for its owner (docs/viewer-sharing.md). */}
                      <td title={row.proposalId}>{label}</td>
                      <td>{row.username}</td>
                      <td>{agencyLabel(row.organizationId, row.organizationId ? agencyName(row.organizationId) : null)}</td>
                      <td className="credits-admin-row-actions">
                        {row.refunded ? (
                          <span className="credits-admin-muted">Refunded</span>
                        ) : (
                          <button type="button" className="credits-admin-link" onClick={() => void refund(row.proposalId, label)}>
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="credits-card-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Unset({ children }: { children: ReactNode }): ReactElement {
  return <span className="credits-card-unset">{children}</span>;
}

function PoolUsage({ rule, usage }: { rule: CreditRule; usage: CreditLimitStatus | null | undefined }): ReactElement {
  if (!usage) return <>{describeAllowance(rule)}</>;
  const share = usage.credits > 0 ? Math.min(1, usage.used / usage.credits) : 1;
  return (
    <span className="credits-card-pool">
      <span
        className={`credits-card-meter${share >= 1 ? ' is-full' : share >= 0.8 ? ' is-low' : ''}`}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={usage.credits}
        aria-valuenow={usage.used}
        aria-label={`${usage.used} of ${usage.credits} used`}
      >
        <span style={{ width: `${share * 100}%` }} />
      </span>
      <span>
        {describeUsage(usage)}
        <span className="credits-admin-muted"> · {rule.window === 'none' ? 'never resets' : `${rule.credits} a ${rule.window}`}</span>
      </span>
    </span>
  );
}

function FormActions({ saving, error, onCancel }: { saving: boolean; error: string | null; onCancel: () => void }): ReactElement {
  return (
    <>
      {error ? <p className="credits-admin-error">{error}</p> : null}
      <div className="credits-admin-actions">
        <button type="submit" className="credits-admin-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="credits-admin-secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
}

function WindowSelect({ value, onChange, label }: { value: RuleWindow; onChange: (w: RuleWindow) => void; label: string }): ReactElement {
  return (
    <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value as RuleWindow)}>
      {WINDOW_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function AmountField({
  label,
  hint,
  credits,
  window,
  onCredits,
  onWindow,
}: {
  label: string;
  hint: string;
  credits: string;
  window: RuleWindow;
  onCredits: (v: string) => void;
  onWindow: (w: RuleWindow) => void;
}): ReactElement {
  return (
    <div className="credits-admin-field">
      <span>{label}</span>
      <span className="credits-amount">
        <input
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          aria-label={`${label} proposals`}
          placeholder="No limit"
          value={credits}
          onChange={(event) => onCredits(event.target.value)}
        />
        <WindowSelect label={`${label} resets`} value={window} onChange={onWindow} />
      </span>
      <span className="credits-field-hint">{hint}</span>
    </div>
  );
}

/** Looks the agency's name up from its id as it's typed (300 ms after the last key). */
function useAgencyLookup(id: string, known: Map<string, string | null>, enabled: boolean) {
  const [state, setState] = useState<{ id: string; name: string | null; loading: boolean }>({ id: '', name: null, loading: false });
  useEffect(() => {
    const key = id.trim();
    if (!enabled || !key) {
      setState({ id: key, name: null, loading: false });
      return;
    }
    const cached = known.get(key);
    if (cached) {
      setState({ id: key, name: cached, loading: false });
      return;
    }
    setState({ id: key, name: null, loading: true });
    let live = true;
    const timer = setTimeout(() => {
      lookupCreditAgency(key)
        .then((found) => live && setState({ id: key, name: found.name, loading: false }))
        .catch(() => live && setState({ id: key, name: null, loading: false }));
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [id, known, enabled]);
  return state;
}

function AgencyForm({
  draft,
  isNew,
  agencies,
  onChange,
  onSubmit,
  actions,
}: {
  draft: AgencyDraft;
  isNew: boolean;
  agencies: CreditAdminResponse['agencies'];
  onChange: (patch: Partial<AgencyDraft>) => void;
  onSubmit: (event: FormEvent) => void;
  actions: ReactNode;
}): ReactElement {
  const known = useMemo(() => new Map(agencies.map((a) => [a.id, a.name])), [agencies]);
  const lookup = useAgencyLookup(draft.organizationId, known, isNew);

  // The looked-up name rides along on the rules, so the card reads even if the lookup is down later.
  useEffect(() => {
    if (isNew && lookup.id === draft.organizationId.trim() && !lookup.loading) {
      if ((lookup.name ?? '') !== draft.organizationName) onChange({ organizationName: lookup.name ?? '' });
    }
  }, [isNew, lookup, draft.organizationId, draft.organizationName, onChange]);

  return (
    <form className={isNew ? 'credits-admin-form' : 'credits-card-form'} onSubmit={onSubmit}>
      {isNew ? <h2 className="credits-admin-subtitle">Add agency</h2> : null}
      <div className="credits-admin-fields">
        {isNew ? (
          <label className="credits-admin-field credits-admin-field-wide">
            <span>VisitIQ agency id</span>
            <input
              list="credits-admin-agencies"
              value={draft.organizationId}
              onChange={(event) => onChange({ organizationId: event.target.value })}
              placeholder="e.g. 2298"
              autoFocus
            />
            <datalist id="credits-admin-agencies">
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id}>
                  {agency.name ?? agency.id}
                </option>
              ))}
            </datalist>
            <span className={`credits-field-hint${lookup.id && !lookup.loading && !lookup.name ? ' is-warning' : ''}`}>
              {!lookup.id
                ? 'Type the id or pick from the list.'
                : lookup.loading
                  ? 'Looking it up…'
                  : lookup.name
                    ? lookup.name
                    : `No VisitIQ agency found with id ${lookup.id}. Check it before saving.`}
            </span>
          </label>
        ) : null}
        <AmountField
          label="Total for the agency"
          hint="One pool every rep shares. Blank = no shared limit."
          credits={draft.totalCredits}
          window={draft.totalWindow}
          onCredits={(totalCredits) => onChange({ totalCredits })}
          onWindow={(totalWindow) => onChange({ totalWindow })}
        />
        <AmountField
          label="Per rep"
          hint="Each rep's own limit. Blank = no per-rep limit."
          credits={draft.perRepCredits}
          window={draft.perRepWindow}
          onCredits={(perRepCredits) => onChange({ perRepCredits })}
          onWindow={(perRepWindow) => onChange({ perRepWindow })}
        />
        <label className="credits-admin-field credits-admin-field-wide">
          <span>Note (optional)</span>
          <input value={draft.note} onChange={(event) => onChange({ note: event.target.value })} placeholder="Pilot plan, renews Nov 1" />
        </label>
      </div>
      {!isNew && draft.totalWindow === 'none' ? (
        <p className="credits-admin-muted">Raising a never-resets total adds credits. Proposals already built still count.</p>
      ) : null}
      {actions}
    </form>
  );
}

function LimitForm({
  draft,
  askEmail,
  lockEmail,
  onChange,
  onSubmit,
  actions,
}: {
  draft: LimitDraft;
  askEmail: boolean;
  lockEmail: boolean;
  onChange: (patch: Partial<LimitDraft>) => void;
  onSubmit: (event: FormEvent) => void;
  actions: ReactNode;
}): ReactElement {
  return (
    <form className="credits-card-form" onSubmit={onSubmit}>
      <div className="credits-admin-fields">
        {askEmail ? (
          <label className="credits-admin-field">
            <span>Rep email</span>
            <input
              type="email"
              value={draft.username}
              disabled={lockEmail}
              onChange={(event) => onChange({ username: event.target.value })}
              placeholder="rep@agency.com"
              autoFocus
            />
          </label>
        ) : null}
        <AmountField
          label={askEmail ? 'Their limit' : 'Each rep'}
          hint={askEmail ? 'Replaces the per-rep limit for this rep. The agency total still applies.' : 'Applies where an agency sets no per-rep limit.'}
          credits={draft.credits}
          window={draft.window}
          onCredits={(credits) => onChange({ credits })}
          onWindow={(window) => onChange({ window })}
        />
        <label className="credits-admin-field credits-admin-field-wide">
          <span>Note (optional)</span>
          <input value={draft.note} onChange={(event) => onChange({ note: event.target.value })} />
        </label>
      </div>
      {actions}
    </form>
  );
}
